/**
 * Справочные списки для шага «Work Activity & Energy».
 *
 * Вынесено из index.jsx, чтобы логику схлопывания дублей и алфавитного
 * порядка можно было проверять тестом отдельно от React-компонента.
 */
export const normalizeName = value => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const nameOf = item => item?.value || item?.name || '';

// Алфавит без учёта регистра — этого просила безопасность для всех справочных
// списков JHA (Work Activity, Energy Source, Hazards, Actions).
export const byName = (a, b) => nameOf(a).localeCompare(nameOf(b), 'en', { sensitivity: 'base' });

export function sortWorkActivities(list) {
    return [...(list || [])].sort(byName);
}

/**
 * Список источников энергии: дубли схлопнуты по имени, всё по алфавиту.
 *
 * Дубли приезжают из справочника — в БД лежат две партии одних и тех же
 * источников, из-за чего «Mobile Equipment» показывался дважды. Бэкенд отдаёт
 * список уже схлопнутым; здесь то же самое делается ещё раз, потому что на
 * устройстве в IndexedDB может лежать выгрузка, скачанная до этого фикса.
 */
export function sortEnergyList(list) {
    const merged = new Map();

    (list || []).forEach((item, index) => {
        const key = normalizeName(item?.value) || `energy-${index}`;
        const hazards = Array.isArray(item?.hazards) ? item.hazards : [];
        const existing = merged.get(key);

        if (!existing) {
            merged.set(key, { ...item, hazards: [...hazards] });
            return;
        }

        // Хазарды копий дополняют друг друга, а не затирают
        const seenHazards = new Set(existing.hazards.map(hazard => normalizeName(nameOf(hazard))));
        hazards.forEach(hazard => {
            const hazardKey = normalizeName(nameOf(hazard));
            if (hazardKey && !seenHazards.has(hazardKey)) {
                seenHazards.add(hazardKey);
                existing.hazards.push(hazard);
            }
        });
        if (!existing.description && item?.description) {
            existing.description = item.description;
        }
    });

    const seenKeys = new Map();

    return Array.from(merged.values())
        .sort(byName)
        .map((item, index) => {
            const baseKey = item.id || item.ID || item.value || `energy-${index}`;
            const count = seenKeys.get(baseKey) || 0;
            seenKeys.set(baseKey, count + 1);
            const uniqueKey = count ? `${baseKey}-${count}` : baseKey;

            const hazards = [...item.hazards]
                .sort(byName)
                .map((hazard, hazardIndex) => ({
                    ...hazard,
                    preventHazards: Array.isArray(hazard.preventHazards)
                        ? [...hazard.preventHazards].sort(byName)
                        : hazard.preventHazards,
                    _key:
                        hazard.id ||
                        hazard.ID ||
                        `${uniqueKey}-hazard-${hazard.name || hazard.value || hazardIndex}-${hazardIndex}`
                }));

            return {
                ...item,
                _key: uniqueKey,
                hazards
            };
        });
}

